import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { theme } from '../styles/theme.js';

export interface OverflowMenuItem {
  id: string;
  label: string;
  /** Emoji string (legacy) */
  icon?: string;
  /** Raw SVG markup string for inline icon (preferred over emoji) */
  iconSvg?: string;
  danger?: boolean;
  toggle?: boolean;
  toggleActive?: boolean;
  disabled?: boolean;
  dividerBefore?: boolean;
}

@customElement('overflow-menu')
export class OverflowMenu extends LitElement {
  static styles = [
    theme,
    css`
      :host {
        display: inline-block;
        position: relative;
      }

      .trigger {
        background: var(--bg-glass);
        border: 1px solid var(--border-glass);
        color: var(--t-muted);
        padding: 4px 8px;
        border-radius: var(--radius-sm);
        cursor: pointer;
        font-size: var(--text-lg);
        line-height: 1;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .trigger:hover {
        color: var(--t-primary);
        background: var(--bg-glass-strong);
        border-color: var(--accent-primary);
      }
    `,
  ];

  @property({ type: Array })
  items: OverflowMenuItem[] = [];

  @state()
  private _open = false;

  private _portal: HTMLDivElement | null = null;
  private _boundClose = this._handleOutsideClick.bind(this);

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this._boundClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this._boundClose);
    this._removePortal();
  }

  private _handleOutsideClick(e: MouseEvent) {
    if (this._open) {
      const path = e.composedPath();
      const trigger = this.shadowRoot?.querySelector('.trigger');
      if (trigger && path.includes(trigger)) return;
      if (this._portal && path.includes(this._portal)) return;
      this._close();
    }
  }

  render() {
    return html`
      <button
        class="trigger"
        @click=${(e: Event) => this._toggle(e)}
        title="More actions"
        aria-label="More actions"
      >
        ⋯
      </button>
    `;
  }

  private _toggle(e: Event) {
    e.stopPropagation();
    if (this._open) {
      this._close();
    } else {
      this._openMenu();
    }
  }

  /** Read a CSS variable from the nearest beam-app host element */
  private _getVar(name: string, fallback: string): string {
    // Walk up to find the beam-app host that defines theme variables
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    let el: HTMLElement | null = this;
    while (el) {
      const val = getComputedStyle(el).getPropertyValue(name).trim();
      if (val) return val;
      // Cross shadow boundary
      el = ((el.getRootNode() as ShadowRoot)?.host as HTMLElement) || el.parentElement;
      if (el === document.documentElement) break;
    }
    return fallback;
  }

  private _openMenu() {
    const trigger = this.shadowRoot?.querySelector('.trigger') as HTMLElement;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    this._open = true;

    // Read theme variables from host (portal is on document.body, outside shadow DOM)
    const bgPanel = this._getVar('--bg-panel', '#1a1a2e');
    const borderGlass = this._getVar('--border-glass', 'rgba(255,255,255,0.08)');
    const tPrimary = this._getVar('--t-primary', '#e2e8f0');
    const tMuted = this._getVar('--t-muted', '#94a3b8');
    const bgGlass = this._getVar('--bg-glass', 'rgba(255,255,255,0.05)');
    const accentPrimary = this._getVar('--accent-primary', '#7c3aed');
    const colorError = this._getVar('--color-error', '#f87171');
    const radiusMd = this._getVar('--radius-md', '12px');
    const radiusSm = this._getVar('--radius-sm', '6px');

    // Create portal on document.body to escape all overflow/containment
    this._removePortal();
    const portal = document.createElement('div');
    // Initially hidden for measurement
    portal.style.cssText = `
      position: fixed;
      top: 0;
      left: -9999px;
      z-index: 99999;
      min-width: 220px;
      background: ${bgPanel};
      border: 1px solid ${borderGlass};
      border-radius: ${radiusMd};
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
      font-family: 'DM Sans', sans-serif;
      padding: 4px 0;
    `;

    this.items.forEach((item) => {
      if (item.dividerBefore) {
        const divider = document.createElement('div');
        divider.style.cssText = `height: 1px; background: ${borderGlass}; margin: 4px 0;`;
        portal.appendChild(divider);
      }

      const btn = document.createElement('button');
      btn.style.cssText = `
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 10px 14px;
        cursor: pointer;
        color: ${item.danger ? colorError : tPrimary};
        font-size: 0.85rem;
        border: none;
        background: none;
        width: 100%;
        text-align: left;
        transition: background 0.15s ease;
        font-family: inherit;
        ${item.toggle ? 'justify-content: space-between;' : ''}
      `;
      btn.onmouseenter = () => {
        btn.style.background = item.danger ? 'hsla(0, 80%, 60%, 0.1)' : bgGlass;
      };
      btn.onmouseleave = () => {
        btn.style.background = 'none';
      };

      if (item.toggle) {
        const labelSpan = document.createElement('span');
        labelSpan.style.cssText = 'display:flex;align-items:center;gap:8px;';
        if (item.iconSvg || item.icon) {
          const iconSpan = document.createElement('span');
          iconSpan.style.cssText =
            'display:inline-flex;align-items:center;justify-content:center;width:20px;height:20px;color:' +
            tMuted +
            ';';
          if (item.iconSvg) {
            iconSpan.innerHTML = item.iconSvg;
          } else {
            iconSpan.style.cssText = 'font-size:0.95rem;width:20px;text-align:center;';
            iconSpan.textContent = item.icon!;
          }
          labelSpan.appendChild(iconSpan);
        }
        const textSpan = document.createElement('span');
        textSpan.textContent = item.label;
        labelSpan.appendChild(textSpan);
        btn.appendChild(labelSpan);

        const toggle = document.createElement('span');
        const toggleBg = item.toggleActive ? accentPrimary : tMuted;
        toggle.style.cssText = `
          width: 36px; height: 20px;
          background: ${toggleBg};
          border-radius: 10px; position: relative; flex-shrink: 0;
          opacity: ${item.toggleActive ? '1' : '0.4'};
          transition: all 0.2s ease;
        `;
        const knob = document.createElement('span');
        knob.style.cssText = `
          position: absolute; top: 3px; left: 3px;
          width: 14px; height: 14px; background: white;
          border-radius: 50%; transition: transform 0.2s ease;
          box-shadow: 0 1px 3px rgba(0,0,0,0.3);
          ${item.toggleActive ? 'transform: translateX(16px);' : ''}
        `;
        toggle.appendChild(knob);
        btn.appendChild(toggle);
      } else {
        if (item.iconSvg || item.icon) {
          const iconSpan = document.createElement('span');
          if (item.iconSvg) {
            iconSpan.style.cssText =
              'display:inline-flex;align-items:center;justify-content:center;width:20px;height:20px;flex-shrink:0;color:' +
              tMuted +
              ';';
            iconSpan.innerHTML = item.iconSvg;
          } else {
            iconSpan.style.cssText =
              'font-size:0.95rem;width:20px;text-align:center;flex-shrink:0;';
            iconSpan.textContent = item.icon!;
          }
          btn.appendChild(iconSpan);
        }
        const textSpan = document.createElement('span');
        textSpan.textContent = item.label;
        btn.appendChild(textSpan);
      }

      // Capture toggle/knob refs for click handler (only exists for toggle items)
      const toggleEl = item.toggle ? (btn.children[1] as HTMLElement) : null;
      const knobEl = toggleEl?.children[0] as HTMLElement | null;

      btn.onclick = (e) => {
        e.stopPropagation();
        if (item.disabled) return;
        if (item.toggle && toggleEl && knobEl) {
          item.toggleActive = !item.toggleActive;
          toggleEl.style.background = item.toggleActive ? accentPrimary : tMuted;
          toggleEl.style.opacity = item.toggleActive ? '1' : '0.4';
          knobEl.style.transform = item.toggleActive ? 'translateX(16px)' : '';
        } else if (!item.toggle) {
          this._close();
        }
        this.dispatchEvent(
          new CustomEvent('menu-select', {
            detail: { id: item.id, item },
            bubbles: true,
            composed: true,
          })
        );
      };

      portal.appendChild(btn);
    });

    document.body.appendChild(portal);

    // Measure actual size, then position within viewport
    const portalRect = portal.getBoundingClientRect();
    const menuW = portalRect.width;
    const menuH = portalRect.height;

    // Align right edge of menu with right edge of trigger
    let left = rect.right - menuW;
    let top = rect.bottom + 4;

    // Clamp horizontally
    if (left < 8) left = 8;
    if (left + menuW > window.innerWidth - 8) left = window.innerWidth - menuW - 8;

    // Flip above trigger if not enough space below
    if (top + menuH > window.innerHeight - 8) {
      top = rect.top - menuH - 4;
    }

    // Clamp to viewport (top and bottom)
    if (top < 8) top = 8;
    const availableH = window.innerHeight - top - 8;
    if (menuH > availableH) {
      portal.style.maxHeight = `${availableH}px`;
      portal.style.overflowY = 'auto';
    }

    portal.style.left = `${left}px`;
    portal.style.top = `${top}px`;

    this._portal = portal;
  }

  private _close() {
    this._open = false;
    this._removePortal();
  }

  private _removePortal() {
    if (this._portal) {
      this._portal.remove();
      this._portal = null;
    }
  }

  close() {
    this._close();
  }
}
